import { Link, Stack, Text } from '@chakra-ui/react'
import { InputFormControl, SelectFormControl } from 'BrokerTsLib/src/components'
import type { BaseAddressDto } from 'BrokerTsLib/src/dtos'
import { useFormContext } from 'BrokerTsLib/src/rhf'
import { parse } from 'BrokerTsLib/src/Utils/formatInputs'
import { useEffect, useMemo } from 'react'
import { mask } from 'remask'

import { FormGroup } from '@/components/FormGroup'
import { useRegions } from '@/hooks'
import { useNameof } from '@/hooks/useNameof'

export const formatPsc = (value?: string) => {
  if (!value) return undefined

  return mask(value.toString(), ['SSS SS'])
}

export const RegionSelector = ({ addressPrefix, isDisabled = false }:
{
  addressPrefix: string,
  street?: boolean,
  isDisabled?: boolean,
  isDisabledStreet?: boolean
}) => {

  const banameof = useNameof<BaseAddressDto>(addressPrefix)
  const pcPath = banameof(x => x.postCode)
  const cityPath = banameof(x => x.city)

  const {
    watch,
    getFieldState,
    setValue
  } = useFormContext()

  const postCodeField = watch(pcPath) ?? ''
  const parsedPostCode = parse(postCodeField)
  const formattedPostCode = formatPsc(parsedPostCode) ?? ''

  const pcError = getFieldState(pcPath).error

  const validPostCode = parse(formattedPostCode).length == 5 && !pcError

  const { data: citiesData, isLoading: isLoadingCities } = useRegions(postCodeField, { enabled: validPostCode })
  const cities = validPostCode ? citiesData?.filter(x => (x.city?.length ?? 0) > 0) ?? [] : []

  // predvybrani obce, pokud je v nabidce jen jedna
  useEffect(() => {
    if (cities.length == 1)
      setValue(cityPath, cities[0].city)
  }, [cities.length])

  const handlePscChange = () => {
    setValue(cityPath, undefined)
  }

  const city = watch(cityPath)
  const citiesOptions = useMemo(() => {
    const options = cities.map(x => ({ label: x.city, value: x.city }))
    if (city && !options.find(x => x.value === city))
      options.unshift({ label: city, value: city })

    return options.sort((a, b) => a.label.localeCompare(b.label))
  }, [cities, city])

  return <Stack>
    <FormGroup>
      <InputFormControl name={pcPath}
        isRequired
        label="PSČ"
        placeholder="### ##"
        autoComplete="postal-code"
        htmlSize={4}
        isDisabled={isDisabled}
        onChange={handlePscChange}
      />

      <SelectFormControl name={cityPath}
        isRequired
        label="Město"
        unselectedText='Vyberte obec'
        isDisabled={!validPostCode || cities.length === 0 || isDisabled}
        options={citiesOptions}
        isLoading={isLoadingCities}
      />
    </FormGroup>
    {!isDisabled &&
            <Text fontSize='sm' color='gray.600'>
              Údaj lze nalézt na webových stránkách{' '}
              <Link href="https://www.psc.cz" target="_blank" rel="noreferrer" variant='underline'>www.psc.cz</Link>.
            </Text>
    }
  </Stack>
}